export default function printHtml(html: string) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  let cleanedUp = false;
  const restoreFocus = () => {
    // Return focus to the app so barcode scanners keep working after the
    // print dialog (focus otherwise often stays on the hidden iframe).
    window.focus();
  };
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    restoreFocus();
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  const printWindow = iframe.contentWindow;
  const doc = printWindow?.document;
  if (!printWindow || !doc) {
    cleanup();
    return;
  }

  // Wait for every image (logos, QR codes, etc.) to finish loading. Printing
  // before images are ready produces a blank or partial label.
  const waitForImages = (): Promise<void> => {
    const images = Array.from(doc.images ?? []);
    return Promise.all(
      images.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.addEventListener("load", () => resolve(), { once: true });
              img.addEventListener("error", () => resolve(), { once: true });
            })
      )
    ).then(() => undefined);
  };

  const triggerPrint = () => {
    // Remove the iframe only once the print dialog has been dealt with,
    // otherwise tearing it down too early cancels the print.
    printWindow.addEventListener("afterprint", () => cleanup(), { once: true });

    try {
      printWindow.focus();
      printWindow.print();
      // Chromium blocks on print() until the dialog closes; restore immediately.
      restoreFocus();
    } catch {
      cleanup();
    }
  };

  doc.open();
  doc.write(html);
  doc.close();

  const onReady = () => {
    waitForImages().then(triggerPrint);
  };

  if (doc.readyState === "complete") {
    onReady();
  } else {
    printWindow.addEventListener("load", onReady, { once: true });
  }
}
